import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PaginationMeta } from "@/lib/types"

interface Props {
  meta: PaginationMeta
  onPageChange: (page: number) => void
  className?: string
}

export default function Pagination({ meta, onPageChange, className }: Props) {
  const { page, pages, count } = meta

  if (pages <= 1) return null

  const canPrev = page > 1
  const canNext = page < pages

  const pageNumbers = buildPageNumbers(page, pages)

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className={cn("flex items-center justify-between gap-2", className)}
    >
      <p className="text-sm text-muted-foreground">
        {count} {count === 1 ? "item" : "items"}
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          ‹
        </Button>

        {pageNumbers.map((p, i) =>
          p === null ? (
            <span key={`ellipsis-${i}`} className="px-2 text-sm text-muted-foreground select-none">
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? "page" : undefined}
              aria-label={`Page ${p}`}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-label="Next page"
        >
          ›
        </Button>
      </div>
    </nav>
  )
}

function buildPageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages: (number | null)[] = [1]

  if (current > 3) pages.push(null)

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - 2) pages.push(null)

  pages.push(total)

  return pages
}
